import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { PlatformId } from "views/page-content/platforms/types";
import CheckmarkIcon from "assets/icons/checkmark-filled.svg?react";
import ErrorIcon from "assets/icons/exclamation.svg?react";
import styles from "../index.module.scss";
import { useTranslation } from "react-i18next";
import { Spinner } from "components/spinners/Spinner";
import { DestinationProcessingStatus } from "..";

export const ProcessingDestination = ({
    destination,
    status,
    error,
    onRetry
}: {
    destination: DestinationDescriptor;
    status: DestinationProcessingStatus;
    error?: string;
    /** Omit to hide the retry link entirely (e.g. after a hard failure). */
    onRetry?: (destinationId: string) => void;
}) => {
    const { t } = useTranslation();
    const { showInCatalog } = useSelector(
        (state: RootState) => state.platforms.general.generalForm
    );
    const { playlistIds } = useSelector(
        (state: RootState) => state.platforms.videoplayer.videoPlayerForm
    );

    let specificDestinationName = destination.name;
    if (destination.platform === PlatformId.Facebook) {
        const precursor =
            destination.platformSpecificSettings?.edge === "me"
                ? `Timeline (${destination.platformSpecificSettings?.selectedVisibility?.name})`
                : "Page";
        specificDestinationName += ` - ${precursor}`;
    }

    let fullDestinationName = `${t(
        `platforms:${destination.platform}`
    )} - ${specificDestinationName}`;

    if (destination.platform === PlatformId.VideoPlayer) {
        const catalogCopy = t("simulcast:video-catalog");
        const collectionsCopy = t("video-upload:collection-count", {
            count: playlistIds.length,
            plural: playlistIds.length > 1 ? t("video-upload:plural") : ""
        });

        const hasCollections = playlistIds.length > 0;
        if (showInCatalog && hasCollections) {
            fullDestinationName = `${catalogCopy} - ${collectionsCopy}`;
        } else if (showInCatalog) {
            fullDestinationName = catalogCopy;
        } else if (hasCollections) {
            fullDestinationName = collectionsCopy;
        }
    }

    const indicatorIcon = useMemo(() => {
        switch (status) {
            case DestinationProcessingStatus.Processing:
                return <Spinner size={20} alignment="right" />;
            case DestinationProcessingStatus.Success:
                return <CheckmarkIcon className={styles.checkmark} />;
            case DestinationProcessingStatus.Error:
                return (
                    <div className={styles["error-indicator"]}>
                        <ErrorIcon className={styles.exclamation} />
                        {onRetry && (
                            <button
                                type="button"
                                className={styles["retry-link"]}
                                onClick={() => onRetry(destination.id)}
                                aria-label={t(
                                    "simulcast:retry-destination-aria",
                                    { destination: fullDestinationName }
                                )}
                            >
                                {t("buttons:retry")}
                            </button>
                        )}
                    </div>
                );
            default:
                return null;
        }
    }, [status, onRetry, destination.id, fullDestinationName, t]);

    const filteredError = useMemo(() => {
        if (!error) return null;
        // For certain specific errors, show a generic error message instead
        const lowerError = error.toLowerCase();
        const ignoredSubstrings = [
            "(400)" // YouTube "invalidValue" errors - users can't change any of the erroring values
        ];

        for (const substr of ignoredSubstrings) {
            if (lowerError.includes(substr)) {
                return t("errors:generic-livestream-error");
            }
        }
        return error;
    }, [error, t]);

    return (
        <li className={`${styles["processing-destination"]} ${styles[status]}`}>
            <p>{fullDestinationName}</p>
            {indicatorIcon}
            {filteredError && (
                <p className={styles["destination-specific-filteredError"]}>
                    {filteredError}
                </p>
            )}
        </li>
    );
};
